"use client"

import styled from "@emotion/styled"
import { Box, IconButton, Modal } from "@mui/material"
import { useTranslations } from "next-intl"
import { SetStateAction, useCallback, useState } from "react"
import { SuccessFailJob, XIcon } from "ui"
import { sharedColors } from "ui/theme/colors"
import WhiteBgHeading from "../../Projects/Marketing/ApartmentMarketingForRent/WhiteBgHeading"
import { FormSectionModal } from "../../SingleCareer/FormSection"

const StyledModal = styled(Modal)`
  display: flex;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: auto;
  z-index: 100002;
  height: 90%;

  ${(props) => props.theme.breakpoints.up("md")} {
    inset: 0;
    height: 100%;
  }
`

const ModalCard = styled(Box)`
  position: relative;
  width: 100%;
  max-width: 760px;
  overflow-y: auto;
  background: ${sharedColors.white};
  border-radius: 0 16px 0 0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 16px 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px 20px;
    overflow-y: hidden;
    height: 100%;

    display: flex;
    flex-direction: column;
    padding-inline: 0;
  }
`

const StyledXIconWrapper = styled(IconButton)`
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  gap: 6.154px;
  background: ${sharedColors.white};
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.16);
  border-radius: 100%;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  gap: 12px;
  margin-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: 24px;
  }
`

const CustomWhiteBgHeading = styled(WhiteBgHeading)`
  --heading-font: var(--narkis-condensed);
  padding: 0;

  .modal-title {
    font-size: 32px;
    line-height: 150%;
    font-weight: 400;
    font-family: var(--narkis-condensed);

    strong {
      color: ${sharedColors.newBlack};
      font-weight: 400;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
      line-height: 130%;
    }
  }
`

const GENERAL_JOB_ID = 4076

type ResultState = {
  open: boolean
  state: "success" | "fail"
  isReferral: boolean
}

export type GeneralPositionModalProps = {
  open: boolean
  onClose: () => void
  title?: string | null
  subtitle?: string | null
  jobName?: string | null
}

const GeneralPositionModal = ({ open, onClose }: GeneralPositionModalProps) => {
  const t = useTranslations("Job")
  const tA11y = useTranslations("Accessibility")
  const [resultState, setResultState] = useState<ResultState>({
    open: false,
    state: "success",
    isReferral: false,
  })

  const handleSuccessRedirect = useCallback(() => {
    setResultState((prev) => ({ ...prev, open: false }))
  }, [])

  const handleSubmitResult = useCallback(
    (payload: { state: "success" | "fail"; isReferral: boolean }) => {
      setResultState({ open: true, ...payload })
      onClose()
    },
    [onClose],
  )

  const messages = {
    checkbox: t("checkbox"),
    submitApp: t("submitApp"),
    attach: t("attach"),
    sharePos: t("sharePos"),
    send: t("send"),
    name: t("name"),
    phone: t("phone"),
    email: t("email"),
    mandatory: t("mandatory"),
    mandatoryReferral: t("mandatoryReferral"),
    firstname: t("firstname"),
    lastname: t("lastname"),
    referralName: t("referralName"),
    referralPhone: t("referralPhone"),
    referralEmail: t("referralEmail"),
    candidateFirstname: t("candidateFirstname"),
    candidateLastname: t("candidateLastname"),
    candidatePhone: t("candidatePhone"),
    candidateEmail: t("candidateEmail"),
    candidateAttach: t("candidateAttach"),
  }

  return (
    <>
      <StyledModal open={open} onClose={onClose} aria-labelledby="general-position-modal-title">
        <ModalCard role="dialog" aria-modal="true" aria-labelledby="general-position-modal-title">
          <ModalHeader>
            <StyledXIconWrapper aria-label={tA11y("closeDialog")} onClick={onClose}>
              <XIcon color={sharedColors.black} />
            </StyledXIconWrapper>
            <Box>
              <CustomWhiteBgHeading
                id="general-position-modal-title"
                title={`<strong>הגשת מועמדות</strong>`}
                isCentered={false}
                titleCls="modal-title"
              />
            </Box>
          </ModalHeader>
          <FormSectionModal
            orderId={GENERAL_JOB_ID}
            isHot={false}
            requireCv={true}
            messages={messages}
            onValidSubmit={onClose}
            onSubmitResult={handleSubmitResult}
          />
        </ModalCard>
      </StyledModal>
      <Modal
        open={resultState.open}
        onClose={() => setResultState((prev) => ({ ...prev, open: false }))}
      >
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
          }}
        >
          <SuccessFailJob
            isReferral={resultState.isReferral}
            state={resultState.state}
            setIsSubmitted={(value: SetStateAction<boolean>) =>
              setResultState((prev) => {
                const newOpen =
                  typeof value === "function"
                    ? // eslint-disable-next-line no-unused-vars
                      (value as (prevOpen: boolean) => boolean)(prev.open)
                    : value
                return { ...prev, open: newOpen }
              })
            }
            handleSuccess={handleSuccessRedirect}
          />
        </Box>
      </Modal>
    </>
  )
}

export default GeneralPositionModal
